export type PokerCard = { rank: string; suit: "s" | "h" | "d" | "c" | "x" };

const SUIT_FROM_SYMBOL: Record<string, PokerCard["suit"]> = {
  "♠": "s",
  "♥": "h",
  "♦": "d",
  "♣": "c",
};

export const SUIT_SYMBOL: Record<PokerCard["suit"], string> = {
  s: "♠",
  h: "♥",
  d: "♦",
  c: "♣",
  x: "",
};

const RANK = "(?:10|[2-9TJQKA])";

// 中文牌面俗称 → 标准 rank（圈=Q，勾/钩=J）
const CN_RANK: Record<string, string> = { 圈: "Q", 勾: "J", 钩: "J" };

/**
 * 无花色公共牌的 rank 兜底解析（parseCards 解不出时用）。
 * 从串首连续提取 3–5 个 rank（允许 - / 空格分隔或紧邻），suit 记为 "x"（未知）。
 * 仅还原牌面点数（纹理），不臆造花色；解不出返回 null，调用方原样显示。
 */
export function parseBoardRanks(input?: string | null): PokerCard[] | null {
  if (!input) return null;
  let text = input.replace(/[（(][^）)]*[）)]/g, " ").trim();
  text = text.replace(/[圈勾钩]/g, (c) => CN_RANK[c] || c);
  const m = text.match(new RegExp(`^((?:${RANK})[\\-\\s]?)+`));
  if (!m) return null;
  const ranks = (m[0].match(new RegExp(RANK, "g")) || []).map((r) =>
    r === "10" ? "T" : r,
  );
  if (ranks.length < 3 || ranks.length > 5) return null;
  return ranks.map((rank) => ({ rank, suit: "x" as const }));
}

/**
 * 按牌面质地给 rank-only 公共牌(全 suit="x")赋代表性花色,使渲染呈现 rainbow/two_tone/monotone。
 * 已有真实花色的牌面(如场景 joined)原样保留;paired/null 无花色信息→保持灰牌。
 * 赋的是传达质地的代表花色(非精确花色):rainbow 各色不同、two_tone 两色、monotone 单色。
 */
const TEXTURE_SUIT: Record<string, (i: number, n: number) => PokerCard["suit"]> = {
  rainbow: (i) => (["s", "h", "d", "c"] as const)[i % 4],
  two_tone: (i, n) => (i < Math.ceil(n / 2) ? "s" : "h"),
  monotone: () => "s",
};

export function applyBoardTexture(
  cards: PokerCard[] | null,
  texture?: string | null,
): PokerCard[] | null {
  if (!cards || cards.some((c) => c.suit !== "x")) return cards;
  const fn = texture ? TEXTURE_SUIT[texture] : undefined;
  if (!fn) return cards;
  const n = cards.length;
  return cards.map((c, i) => ({ ...c, suit: fn(i, n) }));
}

/**
 * 统一解析两种牌格式:
 * - 紧凑式 "Tc7d6s" / "9s9h"(rank + shdc)
 * - 花色符号式 "8♦7♠4♠"(rank + ♠♥♦♣)
 * 混杂文本(如 "QJs(Q♦J♦)")尝试抽出符号式牌;解析不出返回 null,调用方原样显示。
 */
export function parseCards(input?: string | null): PokerCard[] | null {
  if (!input) return null;
  const text = input.trim();
  if (!text) return null;

  // 花色符号式(允许出现在更长文本里,取所有匹配)
  const symbolMatches = [
    ...text.matchAll(new RegExp(`(${RANK})([♠♥♦♣])`, "g")),
  ];
  if (symbolMatches.length >= 2) {
    return symbolMatches.map((m) => ({
      rank: m[1] === "10" ? "T" : m[1],
      suit: SUIT_FROM_SYMBOL[m[2]],
    }));
  }

  // 紧凑式:整串必须全部是 rank+suit 对
  if (new RegExp(`^(?:${RANK}[shdc])+$`).test(text)) {
    const pairs = text.match(new RegExp(`${RANK}[shdc]`, "g")) || [];
    return pairs.map((p) => ({
      rank: p.slice(0, -1),
      suit: p.slice(-1) as PokerCard["suit"],
    }));
  }

  return null;
}

export function isRedSuit(suit: PokerCard["suit"]): boolean {
  return suit === "h" || "d" === suit;
}

/**
 * 手牌记法兜底解析("AA" / "AKs" / "45o" 等 parseCards 解不出的两牌记法)。
 * 还原两张 rank，suit 记为 "x"(未知);多牌/带斜杠等歧义记法返回 null,调用方原样显示。
 */
function matchHand(input?: string | null): RegExpMatchArray | null {
  if (!input) return null;
  const text = input.replace(/[（(][^）)]*[）)]/g, "").trim();
  return text.match(new RegExp(`^(${RANK})(${RANK})([sop])?$`, "i"));
}

export function parseHand(input?: string | null): PokerCard[] | null {
  const m = matchHand(input);
  if (!m) return null;
  const norm = (r: string) => (r === "10" ? "T" : r.toUpperCase());
  return [
    { rank: norm(m[1]), suit: "x" },
    { rank: norm(m[2]), suit: "x" },
  ];
}

export type HandRelation = "同花色" | "杂色" | "对子";

/**
 * 手牌记法的花色关系:同花色 / 杂色 / 对子;未标注则 null。
 * suited 关系叫「同花色」而非「同花」(C'):「同花」在牌语境=已成同花(flush),
 * 新手把起手牌 67s 的「同花」读成已成同花(P1 看懂 50% 主因)。「同花色」= 两张手牌花色相同,一眼无歧义。
 */
export function handRelation(
  input?: string | null,
): HandRelation | null {
  const m = matchHand(input);
  if (!m) return null;
  const suf = (m[3] || "").toLowerCase();
  if (suf === "s") return "同花色";
  if (suf === "o") return "杂色";
  const norm = (r: string) => (r === "10" ? "T" : r.toUpperCase());
  if (norm(m[1]) === norm(m[2])) return "对子";
  return null;
}

/**
 * 给 rank-only 手牌(parseHand 产物,suit="x")按记法关系赋**代表性**花色,
 * 使「同花色/杂色/对子」在牌面上可见,而非只靠徽标文字(新手探针缺陷 P)。
 * 赋的是代表性花色(数据本身不含具体花色),确定性规则:
 * - 同花色(s) → 两张同花色(♠+♠),同色即见「同花色」
 * - 杂色(o) → 一黑一红(♠+♥),异色即见「杂色」
 * - 对子(如 AA) → 同点数必不同花色(♠+♥)
 * - 关系未知(裸记法如 "58") → 保持 x 未知灰牌,绝不臆造
 */
export function assignHandSuits(
  cards: PokerCard[],
  relation: HandRelation,
): PokerCard[] {
  if (cards.length !== 2) return cards;
  if (relation === "同花色") {
    return cards.map((c) => ({ ...c, suit: "s" as const }));
  }
  return [{ ...cards[0], suit: "s" as const }, { ...cards[1], suit: "h" as const }];
}

/**
 * 把手牌记号转成中文可读,供标题等展示层使用(数据层原文不动)。
 * "45s"→"同花色45"、"KJo"→"杂色KJ"、"AA"→"对子AA";
 * 无 s/o 后缀且非对子的裸记号(如顺子片段 "89")与 AsKh 等单花色记法原样保留。
 * 词边界守护:不会切坏 "T92" 这类牌面串或 "0.0s" 这类时间文本。
 */
const HAND_TOKEN = new RegExp(
  `\\b(?:10|[2-9TJQKA])(?:10|[2-9TJQKA])[sop]?\\b`,
  "gi",
);

export function handZh(text: string | null | undefined): string {
  if (!text) return "";
  return text.replace(HAND_TOKEN, (token) => {
    const rel = handRelation(token);
    if (!rel) return token;
    const ranks = token.replace(/[sop]$/i, "").toUpperCase().replace(/10/g, "T");
    return `${rel}${ranks}`;
  });
}

/** 中文牌面俗称 → rank 字符(十=T,圈=Q,勾/钩=J),供展示层记法消毒用 */
const CN_RANK_TO_CHAR: Record<string, string> = {
  二: "2",
  三: "3",
  四: "4",
  五: "5",
  六: "6",
  七: "7",
  八: "8",
  九: "9",
  十: "T",
  圈: "Q",
  勾: "J",
  钩: "J",
};

// 负向先行守护:同花听(听牌)/同花色(handZh 已消歧)/同花面/同花顺(成手)不误伤,只消歧「同花+点数」的 suited 起手牌记法
const SUITED_ZH_RE =
  /同花(?!听|色|面|顺)([2-9TJQKAtjqka二三四五六七八九十勾钩圈]{1,3})/g;

/**
 * 展示层标题/行动线消毒(C'):语料用「同花六七/同花AJ」指 suited 起手牌,
 * 新手读成「已成同花」(P1 看懂 50% 主因)→ 转「同花色手牌67/AJ」,一眼即懂、不靠点 chip。
 * 「单花面」→「三张同花色的牌面」。仅改渲染文本,数据层原文不动。
 */
export function sanitizeSuitedZh(text: string | null | undefined): string {
  if (!text) return "";
  return text
    .replace(/单花面/g, "三张同花色的牌面")
    .replace(SUITED_ZH_RE, (_m, ranks: string) => {
      const norm = [...ranks]
        .map((c) => CN_RANK_TO_CHAR[c] ?? c.toUpperCase())
        .join("");
      return `同花色手牌${norm}`;
    });
}

/** "5.0-43.7" / "0.0s–53.3s" → { start, end }(秒) */
export function parseTimeHint(
  hint?: string | null,
): { start: number; end: number | null } | null {
  if (!hint) return null;
  const m = hint
    .replace(/s/gi, "")
    .replace(/[–—]/g, "-")
    .match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const start = Number.parseFloat(m[1]);
  const end = Number.parseFloat(m[2]);
  if (!Number.isFinite(start)) return null;
  return { start, end: Number.isFinite(end) ? end : null };
}
